/**
 * BillService.js
 * Centralised API layer for the Restaurant Billing system.
 * Every method mirrors a route in billingRouter.js.
 */


//const BASE = 'http://31.97.206.32:9005/api/v1'
// const BASE = "http://31.97.206.32:9005/api/v1";

/**
 * BillService.js
 *
 * Thin HTTP client that maps every context action to the correct
 * API route defined in billingRoutes.js.
 *
 * Base URL is read from the REACT_APP_API_URL env variable so you
 * don't have to touch this file when deploying to different environments.
 */

// const BASE = process.env.REACT_APP_API_URL || '/api'

// ─── generic fetch wrapper ────────────────────────────────────────────────────
// async function request(method, path, body) {
//   const opts = {
//     method,
//     headers: { 'Content-Type': 'application/json' },
//   }
//   if (body !== undefined) opts.body = JSON.stringify(body)

//   const res  = await fetch(`${BASE}${path}`, opts)
//   const json = await res.json()

//   if (!json.status) {
//     throw new Error(json.message || 'API error')
//   }
//   return json.data    // most endpoints wrap payload in { status, data, message }
// }
import { ENDPOINTS } from "./ENDPOINT"

const BASE = ENDPOINTS.BASE;

async function request(method, path, body) {
  const token = localStorage.getItem('token')

  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
  }

  if (body !== undefined) {
    opts.body = JSON.stringify(body)
  }

  const res = await fetch(`${BASE}${path}`, opts)

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }

  const text = await res.text()
  const json = text ? JSON.parse(text) : {}

  if (json.status === false) {
    throw new Error(json.message || 'API error')
  }

  return json.data
}

const get    = (path)        => request('GET',    path)
const post   = (path, body)  => request('POST',   path, body)
const put    = (path, body)  => request('PUT',    path, body)
const patch  = (path, body)  => request('PATCH',  path, body)
const del    = (path)        => request('DELETE', path)

// ─────────────────────────────────────────────────────────────────────────────

const BillService = {

  // ── TABLES ────────────────────────────────────────────────────────────────
  getAllTables:  ()     => get('/tables'),
  // Generic CRUD "get" — unlike /tables, this returns EVERY row including
  // inactive ones. Used to find previously-created hidden (is_active: 0)
  // tables, e.g. the per-room tables Room Service creates behind the scenes.
  getAllRaw: (table) => get(`/get?table=${table}`),

  // Last-known-good StayMaster snapshot, so terminals with no StayMaster
  // session of their own still show something in Room Service.
  syncRoomServiceCache: (bookings) => post('/room-service/cache', { bookings }),
  getRoomServiceCache: () => get('/room-service/cache'),
  // Which StayMaster property ids this logged-in restaurant is linked to —
  // used to filter Room Service bookings so a handed-off token that spans
  // several properties doesn't leak another property's rooms.
  getLinkedPropertyIds: () => get('/restaurants/my-property-ids'),
  getTableById: (id)   => get(`/tables/${id}`),
  createTable:  (data) => post('/tables', data),
  updateTable:  (id, data) => put(`/tables/${id}`, data),
  deleteTable:  (id)   => del(`/tables/${id}`),
  setTableStatus: (id, status) => patch(`/tables/${id}/status`, { status }),

  // ── BILL ITEMS ─────────────────────────────────────────────────────────────────
  getBillitemById: (id)   => get(`/bill_items/${id}`),

  // ── ZONES ─────────────────────────────────────────────────────────────────
  getAllZones: () => get('/zones'),

  // ── CATEGORIES ────────────────────────────────────────────────────────────
  getAllCategories: () => get('/categories'),

  // ── MENU ITEMS ────────────────────────────────────────────────────────────
  getAllMenuItems: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString()
    return get(`/menu-items${qs ? `?${qs}` : ''}`)
  },
  getMenuItemById: (id)        => get(`/menu-items/${id}`),
  createMenuItem:  (data)      => post('/menu-items', data),
  updateMenuItem:  (id, data)  => put(`/menu-items/${id}`, data),
  deleteMenuItem:  (id)        => del(`/menu-items/${id}`),

  // ── BILLS ─────────────────────────────────────────────────────────────────
  getBillsByTable: (tableId) => get(`/bills/table/${tableId}`),
  getBillById:     (id)      => get(`/bills/${id}`),

  getClosedBills: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString()
    return get(`/bills/closed${qs ? `?${qs}` : ''}`)
  },

  /** POST /bills  body: { table_id, items?, discount?, note? } */
  createBill: (data) => post('/bills', data),

  /** PUT /bills/:id/items  body: { items: [...] } */
  updateBillItems: (billId, items) => put(`/bills/${billId}/items`, { items }),

  /** PATCH /bills/:id/meta  body: { discount?, note? } */
  updateBillMeta: (billId, fields) => patch(`/bills/${billId}/meta`, fields),

  /** POST /bills/:id/settle  body: { payment_method } */
  settleBill: (billId, paymentMethod, billSummary) =>
    post(`/bills/${billId}/settle`, { payment_method: paymentMethod, payment_summary: billSummary }),

  /** POST /bills/:id/split  body: { item_ids: [...] } */
  splitBill: (billId, itemIds) =>
    post(`/bills/${billId}/split`, { item_ids: itemIds }),

  /** POST /bills/merge-tables  body: { source_table_id, target_table_id } */
  mergeTables: (sourceTableId, targetTableId) =>
    post('/bills/merge-tables', {
      source_table_id: sourceTableId,
      target_table_id: targetTableId,
    }),
mergeBills: (sourceBillId, targetBillId) =>
  post('/bills/merge', {
    source_bill_id: sourceBillId,
    target_bill_id: targetBillId,
  }),
  /** DELETE /bills/:id */
  deleteBill: (billId) => del(`/bills/${billId}`),
    getTableReport: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString()
    return get(`/reports/table-report${qs ? `?${qs}` : ''}`)
  },

  getBillReport: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString()
    return get(`/reports/bill-report${qs ? `?${qs}` : ''}`)
  },
  getParcelItemReport: (filters = {}) => {
  const qs = new URLSearchParams(filters).toString()
  return get(`/reports/parcel-item-report${qs ? `?${qs}` : ''}`)
},
getDrinkItemReport: (filters = {}) => {
  const qs = new URLSearchParams(filters).toString()
  return get(`/reports/drink-item-report${qs ? `?${qs}` : ''}`)
},
getGeneralSettings: () => get('/general-settings'),
getGeneralSettingsList: () => get('/general-settings/list'),
createGeneralSetting: (data) => post('/general-settings/create', data),
updateGeneralSetting: (data) => put('/general-settings/edit', data),
deleteGeneralSetting: (id) => del(`/general-settings/delete?id=${id}`),

  // ── GUEST ORDERS (placed via the StayMaster guest app) ───────────────────
  /** GET /guest-orders?status=pending */
  getGuestOrders: (status) => get(`/guest-orders${status ? `?status=${status}` : ''}`),
  /** PUT /guest-orders/:id  body: { notes?, items: [{menu_item_id, quantity}] }
   * — fix up a still-pending order before confirming it. */
  updateGuestOrder: (id, data) => put(`/guest-orders/${id}`, data),
  /** PUT /guest-orders/:id/confirm — adds the order's items into the room's
   * open bill (opening one if needed) and moves it to status "confirmed". */
  confirmGuestOrder: (id) => put(`/guest-orders/${id}/confirm`),
  /** PUT /guest-orders/:id/cancel — declines it, no bill touched. */
  cancelGuestOrder: (id) => put(`/guest-orders/${id}/cancel`),
}

export default BillService